import { useRef, useCallback, useEffect } from "react";

export type TokenSymbol = "USDC" | "EURC";

export interface TokenPrice {
  symbol: TokenSymbol;
  amount: string;
}

export interface DraftFormState {
  title: string;
  subtitle: string;
  coverImageUrl: string;
  contentMarkdown: string;
  previewMarkdown: string;
  tokens: TokenPrice[];
  category: string;
  topics: string[];
}

interface SavedDraft extends DraftFormState {
  savedAt: number;
}

export function useLocalDraft(key: string | undefined, debounceMs = 1000) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const save = useCallback(
    (data: DraftFormState) => {
      if (!key) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try {
          const saved: SavedDraft = { ...data, savedAt: Date.now() };
          localStorage.setItem(key, JSON.stringify(saved));
        } catch {
          // localStorage full or unavailable — silently skip
        }
      }, debounceMs);
    },
    [key, debounceMs],
  );

  const load = useCallback((): SavedDraft | null => {
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw) as SavedDraft;
    } catch {
      return null;
    }
  }, [key]);

  const clear = useCallback(() => {
    if (!key) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    localStorage.removeItem(key);
  }, [key]);

  // Flush pending debounce on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { save, load, clear };
}
